import { List, ListItem, ListItemButton, Typography } from '@mui/material';
import { StarBorderOutlined, HomeOutlined } from '@mui/icons-material';
import { useNavigate, useLocation, useParams } from 'react-router';
import { useMemo } from 'react';
import type { MouseEvent } from 'react';
import useLocalization from '../../../../hooks/useLocalization';
import useLocalStorage from '../../../../hooks/useLocalStorage';
import { useSports } from '../../../../queries';
import SportListSkeleton from '../../../molecules/skeletons/SportListSkeleton';
import { StyleObj } from '../../../../@types';
import { mergeSx } from '../../../../utils/styles';

const FAVORITE_SPORTS_STORAGE_KEY = 'gola_favorite_sports';

const styles: StyleObj = {
  list: {
    flex: 1,
    overflow: 'auto',
    py: 0,
  },
  listItem: {
    p: 0,
    borderBottom: 1,
    borderColor: 'neutral.700',
  },
  listItemSelected: {
    position: 'relative',
    backgroundColor: 'neutral.700',
    '&::after': {
      content: '""',
      position: 'absolute',
      top: 0,
      bottom: 0,
      right: 0,
      width: 2,
      backgroundColor: 'info.main',
    },
    '& .MuiTypography-root': {
      fontWeight: 600,
    },
  },
  listItemButton: {
    px: 1,
    py: 1,
    '& .MuiListItemIcon-root': {
      minWidth: 24,
    },
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  sportName: {
    flex: 1,
    ml: 1,
    color: 'neutral.200',
  },
  icon: {
    typography: 'h3',
  },
  iconMuted: {
    color: 'neutral.300',
  },
  iconInactive: {
    color: 'neutral.600',
  },
  iconActive: {
    color: 'info.main',
  },
  iconClickable: {
    cursor: 'pointer',
  },
};

type SportsListProps = {
  isLive?: boolean;
  basePath: string;
};

const SportsList = ({ isLive = false, basePath }: SportsListProps) => {
  const [favoriteSports, setFavoriteSports] = useLocalStorage<string[]>(FAVORITE_SPORTS_STORAGE_KEY, []);

  const navigate = useNavigate();
  const location = useLocation();
  const { sportId } = useParams();
  const { t } = useLocalization();

  const {
    data: sportsData,
    isLoading,
    isSuccess,
  } = useSports({
    queryParams: {
      isLive,
    },
  });

  const favoritesSet = useMemo(() => new Set(favoriteSports), [favoriteSports]);

  const sortedSports = useMemo(() => {
    if (!sportsData) return [];

    return [...sportsData].sort((a, b) => {
      const aFav = favoritesSet.has(a.id);
      const bFav = favoritesSet.has(b.id);

      if (aFav && !bFav) return -1;
      if (!aFav && bFav) return 1;

      return 0;
    });
  }, [sportsData, favoritesSet]);

  const navigateTo = (id: string, name: string) => {
    navigate(`${basePath}/${id}`, { state: { name } });
  };

  const navigateHome = () => {
    navigate('/sports');
  };

  const toggleFavorite = (sportId: string, e: MouseEvent) => {
    e.stopPropagation();

    setFavoriteSports((prev) => {
      if (prev.includes(sportId)) {
        return prev.filter((id) => id !== sportId);
      }

      return [...prev, sportId];
    });
  };

  const isHomeSelected = location.pathname === '/sports';

  return (
    <List sx={styles.list}>
      <ListItem sx={mergeSx(styles.listItem, isHomeSelected && styles.listItemSelected)}>
        <ListItemButton sx={styles.listItemButton} onClick={navigateHome} disableGutters>
          <Typography variant='body2' sx={styles.sportName}>
            {t('home')}
          </Typography>
          <HomeOutlined sx={mergeSx(styles.icon, isHomeSelected ? styles.iconActive : styles.iconMuted)} />
        </ListItemButton>
      </ListItem>

      {isLoading ? (
        <SportListSkeleton itemCount={15} />
      ) : (
        isSuccess &&
        sortedSports.map((sport) => (
          <ListItem key={sport.id} sx={mergeSx(styles.listItem, sport.id === sportId && styles.listItemSelected)}>
            <ListItemButton sx={styles.listItemButton} onClick={() => navigateTo(sport.id, sport.name)} disableGutters>
              <Typography variant='body2' sx={styles.sportName} noWrap title={sport.name}>
                {sport.name}
              </Typography>
              <StarBorderOutlined
                sx={mergeSx(
                  styles.icon,
                  styles.iconClickable,
                  favoritesSet.has(sport.id) ? styles.iconActive : styles.iconInactive
                )}
                onClick={(e) => toggleFavorite(sport.id, e)}
              />
            </ListItemButton>
          </ListItem>
        ))
      )}
    </List>
  );
};

export default SportsList;
